<template>
<div class="container">
  <img src="@/assets/mask.png" alt="Background Image" class="background-image" />

  <div class="top-container">
    <div class="logo-container">
      <a href="https://www.argus-project.eu" target="_blank">
        <img src="@/assets/argus_logo_color.png" alt="ARGUS Logo" class="logo" />
      </a>
    </div>
    <h1 class="panoptes-title">Panoptes</h1>
    <div class="settings-row">
      <button
        class="settings-button"
        type="button"
        title="Settings"
        aria-controls="settingsPanel"
        :aria-expanded="settingsOpen ? 'true' : 'false'"
        @click="settingsOpen = !settingsOpen"
      >
        ⚙
      </button>
    </div>
    <div v-if="settingsOpen" id="settingsPanel" class="settings-panel">
      <div class="model-selection">
        <!-- Workspace selector is hidden while only one workspace is available.
        <label for="workspaceSelect">Workspace:</label>
        <select v-model="selectedWorkspace" id="workspaceSelect" :disabled="loading || availableWorkspaces.length === 0">
          <option v-for="workspace in availableWorkspaces" :key="workspace.id" :value="workspace.id">
            {{ workspace.name }}
          </option>
        </select>
        -->

        <label for="providerSelect">Provider:</label>
        <select v-model="selectedProvider" id="providerSelect" :disabled="loading">
          <option value="ollama">Local Ollama</option>
          <option value="remote">Remote model</option>
        </select>

        <label for="modelSelect">Model:</label>
        <select v-model="selectedModel" id="modelSelect" :disabled="loading || availableModels.length === 0">
          <option v-for="model in availableModels" :key="model" :value="model">
            {{ model }}
          </option>
        </select>

        <label for="questionModeSelect">Source:</label>
        <select v-model="questionMode" id="questionModeSelect" :disabled="loading">
          <option value="auto">Auto</option>
          <option value="document">Documents</option>
          <option value="live_data">Live data</option>
        </select>

        <!-- Keep retrieval fixed at top_k=3 for predictable latency. -->
      </div>
    </div>
    <div v-if="questionMode === 'document'" class="status-bar">
      {{ workspaceStatus }}
    </div>
    <div class="chat-box" id="chatBox" ref="chatBox">
      <div v-if="chatMessages.length === 0" class="welcome-panel">
        <p class="welcome-message">
          Hello, I'm ARGUS Panoptes, your heritage preservation assistant. You can ask me general questions about heritage preservation and the ARGUS project. I can also help with the project documents, monitored heritage sites, installed sensors, latest measurements, and sensor values that may need attention.
        </p>
        <div class="welcome-examples">
          <button
            v-for="example in starterExamples"
            :key="example.question"
            type="button"
            class="welcome-example-button"
            :disabled="loading"
            @click="askExampleQuestion(example)"
          >
            <span>{{ example.category }}</span>
            {{ example.label }}
          </button>
        </div>
      </div>
      
      <div v-for="(message, index) in chatMessages" :key="index" class="chat-message"
        :class="message.sender === 'User' ? 'user-message' : 'bot-message'">
        <div class="message-content" v-html="message.content"></div>
        <button
          v-if="message.sender === 'Bot'"
          class="copy-answer-icon"
          type="button"
          :title="copiedMessageIndex === index ? 'Copied' : 'Copy answer'"
          :aria-label="copiedMessageIndex === index ? 'Copied' : 'Copy answer'"
          @click="copyAnswer(index, message)"
        >
          <span v-if="copiedMessageIndex === index" class="copy-answer-check">✓</span>
          <span v-else class="copy-answer-symbol" aria-hidden="true"></span>
        </button>
        <div v-if="message.sender === 'Bot' && message.sourceDecision" class="source-used">
          Source used: {{ message.sourceDecision }}
        </div>
        <details v-if="message.references && message.references.length > 0" class="references-container">
          <summary class="references-summary">
            References ({{ message.references.length }})
          </summary>
          <ul>
            <li v-for="(file, refIndex) in message.references" :key="refIndex">
              <div class="reference-title">
                📄 {{ file.citationLabel || displayReferenceName(file.filename) }}
                <span v-if="file.score">({{ file.score.toFixed(3) }})</span>
              </div>

              <details v-if="file.preview" class="reference-preview">
                <summary>Show excerpt</summary>
                <p>{{ file.supportingExcerpt || file.preview }}</p>
              </details>
            </li>
          </ul>
        </details>
        <div v-if="message.sender === 'Bot' && message.requestId" class="feedback-container">
          <span class="feedback-label">How's this answer?</span>
          <button
            v-for="option in feedbackOptions"
            :key="option.value"
            class="feedback-button"
            :class="{ 'feedback-button-selected': message.feedback === option.value }"
            :disabled="!!message.feedback"
            @click="submitFeedback(index, option.value)"
          >
            {{ option.emoji }} {{ option.label }}
          </button>
          <span v-if="message.feedback && message.feedback !== 'bad'" class="feedback-thanks">Thanks for the feedback!</span>
        </div>
        <div v-if="message.feedback === 'bad' && !message.feedbackCommentSubmitted" class="feedback-comment-container">
          <input
            v-model="message.feedbackComment"
            type="text"
            class="feedback-comment-input"
            placeholder="What was wrong? (optional)"
            @keydown.enter="submitFeedbackComment(index)"
          />
          <button class="feedback-comment-submit" @click="submitFeedbackComment(index)">Submit</button>
        </div>
        <span v-if="message.feedback === 'bad' && message.feedbackCommentSubmitted" class="feedback-thanks">Thanks for the feedback!</span>
      </div>

    </div>
    
    <div class="input-footer-container">
      <div v-if="loading" class="loading-message">Analyzing query and creating response...</div>
      <div class="input-container">
        <input v-model="userMessage" type="text" placeholder="Message ARGUS Panoptes" @keydown.enter="sendMessage"
          :disabled="loading" />
        <button
          :class="{ 'stop-button': loading }"
          @click="loading ? stopRequest() : sendMessage()"
        >
          {{ loading ? 'Stop' : 'Send' }}
        </button>
        <button class="clear-button" @click="clearChat" :disabled="loading || chatMessages.length === 0">Clear chat</button>
      </div>
    </div>

  </div>
</div>
</template>

<script>
import axios from 'axios';
import { marked } from 'marked';

const RAG_API_BASE_URL = process.env.VUE_APP_RAG_API_URL || '/api';

export default {
  data() {
    return {
      userMessage: '',
      chatMessages: [],
      topK: 3,
      questionMode: 'auto',
      settingsOpen: false,
      loading: false,
      activeAskController: null,
      copiedMessageIndex: null,
      selectedProvider: 'ollama',
      selectedModel: '',
      availableModels: [],
      providerModels: {
        ollama: [],
        remote: [],
      },
      selectedWorkspace: '',
      availableWorkspaces: [],
      systemStatus: 'Loading system status...',
      starterExamples: [],
      examplePools: {
        documents: [
          {
            category: 'Documents',
            label: 'Structural monitoring sensor types',
            mode: 'document',
            question: 'What specific sensor types are used for structural health monitoring of heritage buildings?',
          },
          {
            category: 'Documents',
            label: 'Digital twins for conservation',
            mode: 'document',
            question: 'How can digital twins support preventive conservation of cultural heritage?',
          },
          {
            category: 'Documents',
            label: 'Remote sensing for heritage sites',
            mode: 'document',
            question: 'How can remote sensing support monitoring of cultural heritage sites?',
          },
          {
            category: 'Documents',
            label: 'Climate risks for heritage',
            mode: 'document',
            question: 'What climate-related risks are discussed for remote cultural heritage sites?',
          },
          {
            category: 'Documents',
            label: 'ARGUS monitoring technologies',
            mode: 'document',
            question: 'Which ARGUS technologies support cultural heritage monitoring?',
          },
          {
            category: 'Documents',
            label: 'Preventive conservation methods',
            mode: 'document',
            question: 'What methods are used for preventive conservation of cultural heritage?',
          },
        ],
        liveData: [
          {
            category: 'Live data',
            label: 'Sensors per site',
            mode: 'live_data',
            question: 'How many sensors are deployed per site?',
          },
          {
            category: 'Live data',
            label: 'Latest Delos temperatures',
            mode: 'live_data',
            question: 'Give me the last temperature measurements for Delos Island.',
          },
          {
            category: 'Live data',
            label: 'Latest measurements',
            mode: 'live_data',
            question: 'What are the latest 20 readings?',
          },
          {
            category: 'Live data',
            label: 'Readings per site',
            mode: 'live_data',
            question: 'How many readings are available per site?',
          },
          {
            category: 'Live data',
            label: 'Latest timestamp',
            mode: 'live_data',
            question: 'What is the latest reading timestamp overall?',
          },
          {
            category: 'Live data',
            label: 'Measured quantities',
            mode: 'live_data',
            question: 'How many readings are available per observed quantity?',
          },
        ],
        sensorChecks: [
          {
            category: 'Sensor checks',
            label: 'Baltanas vibration check',
            mode: 'live_data',
            question: 'Check Baltanas vibration PPV anomaly using the vibration policy.',
          },
          {
            category: 'Sensor checks',
            label: 'Delos wind check',
            mode: 'live_data',
            question: 'Check Delos wind anomaly using the wind policy.',
          },
          {
            category: 'Sensor checks',
            label: 'Delos rainfall check',
            mode: 'live_data',
            question: 'Check Delos rainfall anomaly using the rainfall 1 hour policy.',
          },
          {
            category: 'Sensor checks',
            label: 'Baltanas rainfall check',
            mode: 'live_data',
            question: 'Check Baltanas rainfall anomaly using the rainfall 1 hour policy.',
          },
          {
            category: 'Sensor checks',
            label: 'Baltanas soil moisture check',
            mode: 'live_data',
            question: 'Check Baltanas soil moisture anomaly using the soil moisture policy.',
          },
          {
            category: 'Sensor checks',
            label: 'Sensor values needing attention',
            mode: 'live_data',
            question: 'Are there any sensor values that may need attention?',
          },
        ],
      },
      feedbackOptions: [
        { value: 'good', label: 'Good', emoji: '🙂' },
        { value: 'fine', label: 'Fine', emoji: '😐' },
        { value: 'bad', label: 'Bad', emoji: '🙁' },
      ],
    };
  },
  mounted() {
    this.randomizeStarterExamples();
    this.loadStatus();
    this.loadModels();
    this.loadWorkspaces();
  },
  computed: {
    selectedWorkspaceInfo() {
      return this.availableWorkspaces.find(
        (workspace) => workspace.id === this.selectedWorkspace
      );
    },
    workspaceStatus() {
      if (!this.selectedWorkspaceInfo) {
        return this.systemStatus;
      }

      return `Chunks: ${this.selectedWorkspaceInfo.chunks} | Sources: ${this.selectedWorkspaceInfo.sources}`;
    },
  },
  watch: {
    selectedProvider() {
      this.syncModelsForProvider();
    },
  },
  methods: {
    randomizeStarterExamples() {
      const pickTwo = (items) => [...items]
        .sort(() => Math.random() - 0.5)
        .slice(0, 2);

      this.starterExamples = [
        ...pickTwo(this.examplePools.documents),
        ...pickTwo(this.examplePools.liveData),
        ...pickTwo(this.examplePools.sensorChecks),
      ];
    },
    async loadStatus() {
      try {
        const response = await axios.get(`${RAG_API_BASE_URL}/status`);
        const data = response.data;

        this.systemStatus = `Chunks: ${data.chunks} | Sources: ${data.sources}`;

        const ollamaModels = data.available_ollama_models || (data.ollama_model ? [data.ollama_model] : []);
        const remoteModels = data.remote_model ? [data.remote_model] : ['qwen3.6:35b'];

        this.providerModels = {
          ...this.providerModels,
          ollama: ollamaModels,
          remote: remoteModels,
        };
        this.syncModelsForProvider();
      } catch (error) {
        this.systemStatus = 'System status unavailable.';
        console.error('Status error:', error);
      }
    },
    async loadModels() {
      try {
        const response = await axios.get(`${RAG_API_BASE_URL}/models`);
        const data = response.data;

        this.providerModels = {
          ...this.providerModels,
          ollama: data.models || [],
        };

        if (this.selectedProvider === 'ollama') {
          this.syncModelsForProvider(data.default_model);
        }
      } catch (error) {
        this.providerModels = {
          ...this.providerModels,
          ollama: [],
        };

        if (this.selectedProvider === 'ollama') {
          this.syncModelsForProvider();
        }
        console.error('Models error:', error);
      }
    },
    async loadWorkspaces() {
      try {
        const response = await axios.get(`${RAG_API_BASE_URL}/workspaces`);
        const data = response.data;

        this.availableWorkspaces = data.workspaces || [];
        this.selectedWorkspace = data.default_workspace || this.availableWorkspaces[0]?.id || '';
      } catch (error) {
        this.availableWorkspaces = [];
        this.selectedWorkspace = '';
        console.error('Workspaces error:', error);
      }
    },
    syncModelsForProvider(preferredModel = '') {
      const models = this.providerModels[this.selectedProvider] || [];
      this.availableModels = models;

      if (preferredModel && models.includes(preferredModel)) {
        this.selectedModel = preferredModel;
      } else if (!models.includes(this.selectedModel)) {
        this.selectedModel = models[0] || '';
      }
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const chatBox = this.$refs.chatBox;
        if (chatBox) {
          chatBox.scrollTop = chatBox.scrollHeight;
        }
      });
    },
    formatAnswer(rawAnswer) {
      if (!rawAnswer) return '';

      let answer = rawAnswer.trim();

      // The backend already returns structured references separately.
      // Remove any source list that the LLM may have generated inside the answer text.
      answer = answer.replace(/^\s*(\*\*)?Sources(\*\*)?\s*:?\s*$[\s\S]*/im, '');

      // Normalize common section labels so they render cleanly as Markdown.
      answer = answer
        .replace(/^Answer\s*:?\s*/i, '**Answer**\n\n')
        .replace(/\n\s*Answer\s*:?\s*/i, '\n\n**Answer**\n\n')
        .replace(/^Live data answer\s*:?\s*/i, '**Live data answer**\n\n')
        .replace(/\n\s*Document answer\s*:?\s*/i, '\n\n**Document answer**\n\n')
        .replace(/\n\s*Key points\s*:?\s*/i, '\n\n**Key points**\n\n')
        .replace(/\n\s*References\s*:?\s*/i, '\n\n**References**\n\n');

      const tablePlaceholders = [];
      answer = answer.replace(/(^\|.*\|\s*\n\|\s*:?-{3,}:?.*\|\s*(?:\n\|.*\|\s*)*)/gm, (table) => {
        const key = `__PANOPTES_TABLE_${tablePlaceholders.length}__`;
        tablePlaceholders.push(table.trim());
        return `\n${key}\n`;
      });

      // Make sure bullet points start on separate Markdown lines without touching tables.
      answer = answer
        .replace(/\s*[•●]\s+/g, '\n- ')
        .replace(/(^|\n)\s+-\s+/g, '\n- ')
        .replace(/\n{3,}/g, '\n\n');

      tablePlaceholders.forEach((table, index) => {
        answer = answer.replace(`__PANOPTES_TABLE_${index}__`, table);
      });

      return answer.trim();
    },
    async copyAnswer(index, message) {
      const text = this.stripHtml(message.content || '').trim();
      if (!text) return;

      const markCopied = () => {
        this.copiedMessageIndex = index;
        window.setTimeout(() => {
          if (this.copiedMessageIndex === index) {
            this.copiedMessageIndex = null;
          }
        }, 1500);
      };

      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(text);
          markCopied();
          return;
        }

        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        markCopied();
      } catch (error) {
        console.error('Failed to copy answer:', error);
      }
    },

    stripHtml(html) {
      const temporaryElement = document.createElement('div');
      temporaryElement.innerHTML = html || '';
      return (temporaryElement.textContent || temporaryElement.innerText || '').replace(/\s+/g, ' ').trim();
    },
    displayReferenceName(filename) {
      if (filename === 'SPARQL query') return 'Database query';
      if (filename === 'Result') return 'Database result';
      return filename;
    },
    displaySourcesUsed(sourcesUsed) {
      const sources = Array.isArray(sourcesUsed) ? sourcesUsed : [];
      const labels = sources.map((source) => {
        if (source === 'live_data') return 'Live data';
        if (source === 'documents') return 'Documents';
        return source;
      }).filter(Boolean);

      return labels.length > 0 ? labels.join(' + ') : '';
    },
    hasMarkdownTable(text) {
      const lines = String(text || '').split('\n').map((line) => line.trim());
      return lines.some((line, index) => {
        const nextLine = lines[index + 1] || '';
        return (
          line.startsWith('|') &&
          line.endsWith('|') &&
          nextLine.startsWith('|') &&
          /^(\|\s*:?-{3,}:?\s*)+\|$/.test(nextLine)
        );
      });
    },
    splitMarkdownTables(text) {
      const lines = String(text || '').split('\n');
      const textLines = [];
      const tableBlocks = [];

      for (let index = 0; index < lines.length; index += 1) {
        const line = lines[index].trim();
        const nextLine = (lines[index + 1] || '').trim();
        const isTableStart = (
          line.startsWith('|') &&
          line.endsWith('|') &&
          nextLine.startsWith('|') &&
          /^(\|\s*:?-{3,}:?\s*)+\|$/.test(nextLine)
        );

        if (!isTableStart) {
          textLines.push(lines[index]);
          continue;
        }

        const tableLines = [lines[index], lines[index + 1]];
        index += 2;
        while (index < lines.length) {
          const tableLine = lines[index].trim();
          if (!tableLine.startsWith('|') || !tableLine.endsWith('|')) break;
          tableLines.push(lines[index]);
          index += 1;
        }
        index -= 1;
        tableBlocks.push(tableLines.join('\n'));
      }

      return {
        textMarkdown: textLines.join('\n').trim(),
        tableMarkdown: tableBlocks.join('\n\n').trim(),
      };
    },
    buildChatHistory() {
      return this.chatMessages
        .slice(-6)
        .map((message) => ({
          role: message.sender === 'User' ? 'user' : 'assistant',
          content: this.stripHtml(message.content),
        }))
        .filter((message) => message.content.length > 0);
    },
    sourceToReference(source) {
      return {
        filename: source.filename,
        score: source.score,
        preview: source.text_preview,
        citationLabel: source.citation_label,
        supportingExcerpt: source.supporting_excerpt,
        title: source.title,
        authors: source.authors,
        year: source.year,
        section: source.section,
        pageStart: source.page_start,
        pageEnd: source.page_end,
        doi: source.doi,
      };
    },
    renderStreamingAnswer(rawAnswer) {
      const botMessage = this.formatAnswer(rawAnswer || '');
      return marked(botMessage);
    },
    shouldUseDirectAsk(question) {
      const mode = String(this.questionMode || 'auto').toLowerCase();
      if (mode === 'live_data' || mode === 'live') return true;
      if (mode === 'document' || mode === 'documents') return false;

      const q = ` ${String(question || '').toLowerCase()} `;
      const directLiveTerms = [
        'how many', 'count', 'latest', 'last', 'recent', 'timestamp',
        'reading', 'readings', 'observation', 'observations',
        'measurement', 'measurements', 'available', 'have any',
        'per site', 'deployed', 'deployment', 'device', 'devices',
        'delos', 'baltanas', 'baltanás', 'ranverso', 'schenkenberg',
        'lucretili', 'house of masks', 'house of the masks',
      ];
      return directLiveTerms.some((term) => q.includes(term));
    },
    async runDirectAsk(question, chatHistory, botIndex) {
      const response = await axios.post(`${RAG_API_BASE_URL}/ask`, {
        question,
        top_k: this.topK,
        llm_provider: this.selectedProvider || 'ollama',
        model: this.selectedModel || null,
        workspace: this.selectedWorkspace || null,
        mode: this.questionMode || 'auto',
        chat_history: chatHistory,
      }, {
        signal: this.activeAskController?.signal,
      });

      const data = response.data || {};
      this.chatMessages[botIndex] = {
        ...this.chatMessages[botIndex],
        content: this.renderStreamingAnswer(data.answer || ''),
        references: (data.retrieved_sources || []).map(this.sourceToReference),
        requestId: data.request_id || null,
        route: data.route || null,
        sourceDecision: data.source_decision || this.displaySourcesUsed(data.sources_used),
      };
      this.scrollToBottom();
    },
    async sendMessage() {
      const question = this.userMessage.trim();

      if (!question || this.loading) return;

      const chatHistory = this.buildChatHistory();

      this.chatMessages.push({ sender: 'User', content: question });
      this.scrollToBottom();
      this.loading = true;
      this.activeAskController = new AbortController();
      this.userMessage = '';

      const botIndex = this.chatMessages.push({
        sender: 'Bot',
        content: '<p>Searching references...</p>',
        references: [],
        requestId: null,
        route: null,
        sourceDecision: null,
        question,
        feedback: null,
      }) - 1;
      this.scrollToBottom();

      let rawAnswer = '';
      let streamBuffer = '';

      try {
        if (this.shouldUseDirectAsk(question)) {
          this.chatMessages[botIndex] = {
            ...this.chatMessages[botIndex],
            content: '<p>Querying live data...</p>',
          };
          this.scrollToBottom();
          await this.runDirectAsk(question, chatHistory, botIndex);
          return;
        }

        console.log('[Panoptes timing] sending /ask/stream request at:', new Date().toISOString());
        console.time('[Panoptes timing] /ask/stream total fetch time');

        const response = await fetch(`${RAG_API_BASE_URL}/ask/stream`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            question,
            top_k: this.topK,
            llm_provider: this.selectedProvider || 'ollama',
            model: this.selectedModel || null,
            workspace: this.selectedWorkspace || null,
            mode: this.questionMode || 'auto',
            chat_history: chatHistory,
          }),
          signal: this.activeAskController.signal,
        });

        if (!response.ok || !response.body) {
          const error = new Error(`Streaming request failed: ${response.status}`);
          error.status = response.status;
          throw error;
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let streamDone = false;

        while (!streamDone) {
          const { value, done } = await reader.read();
          streamDone = done;
          streamBuffer += decoder.decode(value || new Uint8Array(), { stream: !done });

          const lines = streamBuffer.split('\n');
          streamBuffer = lines.pop() || '';

          for (const line of lines) {
            const trimmed = line.trim();
            if (!trimmed) continue;

            const event = JSON.parse(trimmed);

            if (event.type === 'meta') {
              this.chatMessages[botIndex] = {
                ...this.chatMessages[botIndex],
                requestId: null,
                route: event.route || null,
                sourceDecision: event.source_decision || this.displaySourcesUsed(event.sources_used),
              };
            } else if (event.type === 'sources') {
              this.chatMessages[botIndex] = {
                ...this.chatMessages[botIndex],
                content: rawAnswer ? this.renderStreamingAnswer(rawAnswer) : '<p>Generating answer...</p>',
                requestId: null,
                references: (event.retrieved_sources || []).map(this.sourceToReference),
              };
            } else if (event.type === 'token') {
              rawAnswer += event.text || '';
              this.chatMessages[botIndex] = {
                ...this.chatMessages[botIndex],
                content: this.renderStreamingAnswer(rawAnswer),
              };
            } else if (event.type === 'done') {
              rawAnswer = event.answer || rawAnswer;
              this.chatMessages[botIndex] = {
                ...this.chatMessages[botIndex],
                content: this.renderStreamingAnswer(rawAnswer),
                requestId: event.request_id || this.chatMessages[botIndex].requestId,
                route: event.route || this.chatMessages[botIndex].route,
                sourceDecision: event.source_decision || this.chatMessages[botIndex].sourceDecision,
              };
            } else if (event.type === 'error') {
              throw new Error(event.message || 'Streaming error');
            }

            this.scrollToBottom();
          }
        }

        console.timeEnd('[Panoptes timing] /ask/stream total fetch time');
      } catch (error) {
        const stopped = error?.name === 'AbortError';
        const shouldFallbackToAsk = !stopped && error?.status === 400;

        if (shouldFallbackToAsk) {
          try {
            console.log('[Panoptes timing] stream unavailable for this query; falling back to /ask');
            await this.runDirectAsk(question, chatHistory, botIndex);
          } catch (fallbackError) {
            const fallbackStopped = fallbackError?.name === 'AbortError' || fallbackError?.code === 'ERR_CANCELED';
            this.chatMessages[botIndex] = {
              ...this.chatMessages[botIndex],
              content: fallbackStopped ? 'Request stopped.' : 'Error fetching response',
            };
            this.scrollToBottom();
            if (!fallbackStopped) {
              console.error('Fallback /ask error:', fallbackError);
            }
          }
        } else {
          this.chatMessages[botIndex] = {
            ...this.chatMessages[botIndex],
            content: stopped ? 'Request stopped.' : 'Error fetching response',
          };
          this.scrollToBottom();
          if (!stopped) {
            console.error('Error:', error);
          }
        }
      } finally {
        this.loading = false;
        this.activeAskController = null;
      }
    },
    stopRequest() {
      if (this.activeAskController) {
        this.activeAskController.abort();
      }
    },
    clearChat() {
      this.chatMessages = [];
      this.userMessage = '';
    },
    askExampleQuestion(example) {
      if (this.loading) return;

      this.questionMode = example.mode || 'auto';
      this.userMessage = example.question;
      this.sendMessage();
    },
    async submitFeedback(index, rating) {
      const message = this.chatMessages[index];
      if (!message || !message.requestId || message.feedback) return; // one rating per message

      this.chatMessages[index] = {
        ...message,
        feedback: rating,
        feedbackComment: '',
        feedbackCommentSubmitted: false,
      };

      try {
        await axios.post(`${RAG_API_BASE_URL}/feedback`, {
          request_id: message.requestId,
          rating,
          question: message.question || null,
          route: message.route || null,
        });
      } catch (error) {
        console.error('Feedback error:', error);
      }
    },
    async submitFeedbackComment(index) {
      const message = this.chatMessages[index];
      if (!message || !message.requestId || message.feedback !== 'bad' || message.feedbackCommentSubmitted) return;

      const comment = (message.feedbackComment || '').trim();
      this.chatMessages[index] = { ...message, feedbackCommentSubmitted: true };
      if (!comment) return; // rating is already logged; nothing more to send

      try {
        await axios.post(`${RAG_API_BASE_URL}/feedback`, {
          request_id: message.requestId,
          rating: 'bad',
          question: message.question || null,
          route: message.route || null,
          comment,
          comment_only: true,
        });
      } catch (error) {
        console.error('Feedback comment error:', error);
      }
    },
  },
};
</script>


<style>

/* styles.css */
@import url('https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,100..900;1,100..900&display=swap');

/* General Styling */
body {
  font-family: 'Montserrat', sans-serif;
  margin: 0;
  padding: 0;
  background-color: rgb(220, 220, 220);
  color: #333;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  overflow: hidden;
}

h1 {
  font-size: 26px;
  text-align: center;
  color: rgb(54, 55, 85);
  margin: 0 0 4px 0;
}

.panoptes-title {
  letter-spacing: 0.5px;
  animation: panoptes-argus-color-breathing 24s infinite ease-in-out;
}

@keyframes panoptes-argus-color-breathing {
  0% {
    text-shadow: 0 0 0 rgba(255, 202, 78, 0);
  }
  14% {
    text-shadow:
      0 0 5px rgba(255, 202, 78, 0.36),
      0 0 10px rgba(255, 202, 78, 0.18);
  }
  28% {
    text-shadow: 0 0 0 rgba(255, 202, 78, 0);
  }
  43% {
    text-shadow:
      0 0 5px rgba(0, 161, 154, 0.36),
      0 0 10px rgba(0, 161, 154, 0.18);
  }
  57% {
    text-shadow: 0 0 0 rgba(0, 161, 154, 0);
  }
  72% {
    text-shadow:
      0 0 5px rgba(227, 71, 96, 0.36),
      0 0 10px rgba(227, 71, 96, 0.18);
  }
  86% {
    text-shadow: 0 0 0 rgba(227, 71, 96, 0);
  }
  100% {
    text-shadow: 0 0 0 rgba(255, 202, 78, 0);
  }
}

/* Chat box styling */
.chat-box {
  background-color: rgba(255, 255, 255, 0.9);
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 10px 28px 10px;

  position: fixed;
  top: 175px;
  bottom: 190px;
  left: 50%;
  transform: translateX(-50%);

  width: 80%; /* Adjust as needed */
  border-radius: 10px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1); /* Optional: shadow effect */
  scroll-padding-bottom: 32px;
}

.chat-message {
  padding: 8px 10px;
  border-radius: 12px;
  max-width: 95%;
  word-wrap: break-word;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.15);
  flex-shrink: 0;
}

.user-message {
  background-color: rgb(247, 204, 101);
  align-self: flex-end;
  color: rgb(62, 58, 98);
}

.bot-message {
  background-color: rgba(204, 204, 204, 0.9);
}

.message-content {
  line-height: 1.5;
}

.source-used {
  margin-top: 8px;
  font-size: 12px;
  font-weight: 600;
  color: rgb(62, 58, 98);
}

.message-content p {
  margin: 0 0 12px 0;
}

.message-content p:last-child {
  margin-bottom: 0;
}

.bot-message ul {
  margin: 8px 0 8px 22px;
  padding-left: 18px;
}

.bot-message li {
  margin-bottom: 6px;
  line-height: 1.45;
}

.bot-message p {
  line-height: 1.5;
}

.message-content table {
  display: block;
  width: 100%;
  max-width: 100%;
  margin: 10px 0 4px;
  border-collapse: collapse;
  overflow-x: auto;
  white-space: nowrap;
  font-size: 13px;
}

.message-content th,
.message-content td {
  border: 1px solid rgba(54, 55, 85, 0.18);
  padding: 7px 9px;
  text-align: left;
  vertical-align: top;
}

.message-content th {
  background: rgba(54, 55, 85, 0.12);
  color: rgb(54, 55, 85);
  font-weight: 700;
}

.message-content tr:nth-child(even) td {
  background: rgba(255, 255, 255, 0.38);
}

.message-content td {
  background: rgba(255, 255, 255, 0.24);
}




/* Combined input and footer container */
.input-footer-container {
  position: fixed;
  left: 5%; /* To center it with 90% width */
  bottom: 0;
  width: 90%; /* Set width to 90% of the window */
  background-color: rgb(54, 55, 85);
  display: flex; /* Use flexbox for layout */
  flex-direction: column;
  align-items: center;
  z-index: 10;
}

/* Input area styling */
.input-container {
  display: flex;
  padding: 15px;
  background-color: #222;
  border-radius: 10px;
  justify-content: center;
  gap: 10px;
  width: 100%; /* Make it span the full width of the input-footer-container */
}

input[type="text"] {
width: 85%;
padding: 12px;
border-radius: 20px;
border: 2px solid #444;
background-color: #444;
color: white;
font-size: 16px;
margin-right: 10px;
transition: background-color 0.3s ease;
font-family: 'Montserrat', sans-serif;
}
input[type="text"]:focus {
  border: 2px solid rgb(74, 154, 143);
  outline: none;
}


/* Button styling */
button {
  padding: 12px 20px;
  background-color: rgb(62, 58, 98);
  color: white;
  border-radius: 50px;
  border: none;
  font-size: 16px;
  font-family: 'Montserrat', sans-serif;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

button:hover {
  background-color: rgb(255, 255, 255);
  color: rgb(62, 58, 98);
  border: rgb(62, 58, 98);
}

.clear-button {
  background-color: rgb(74, 154, 143);
}

.stop-button {
  background-color: rgb(191, 76, 76);
}

.stop-button:hover {
  background-color: rgb(255, 255, 255);
  color: rgb(191, 76, 76);
}

.clear-button:hover {
  background-color: rgb(255, 255, 255);
  color: rgb(74, 154, 143);
}

.welcome-panel {
  align-self: center;
  width: min(860px, 92%);
  margin: auto;
  padding: 20px;
  color: rgb(54, 55, 85);
  text-align: center;
}

.welcome-message {
  margin: 0 auto 18px;
  max-width: 760px;
  font-size: 18px;
  line-height: 1.5;
  font-weight: 500;
}

.welcome-examples {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.welcome-example-button {
  min-height: 58px;
  padding: 10px 12px;
  border-radius: 8px;
  background-color: rgba(255, 255, 255, 0.85);
  color: rgb(54, 55, 85);
  border: 1px solid rgba(54, 55, 85, 0.18);
  font-size: 13px;
  font-weight: 600;
  line-height: 1.25;
  text-align: left;
}

.welcome-example-button span {
  display: block;
  margin-bottom: 4px;
  color: rgb(74, 154, 143);
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.welcome-example-button:hover:not(:disabled) {
  background-color: rgb(62, 58, 98);
  color: #ffffff;
}

.welcome-example-button:hover:not(:disabled) span {
  color: rgb(247, 204, 101);
}

/* Dropdown and Label Styling */
.settings-row {
  position: absolute;
  top: 14px;
  right: 18px;
  z-index: 20;
}

.settings-button {
  width: 42px;
  height: 42px;
  padding: 0;
  border-radius: 50%;
  border: 2px solid rgb(54, 55, 85);
  background-color: rgba(255, 255, 255, 0.9);
  color: rgb(54, 55, 85);
  font-size: 21px;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.settings-button:hover {
  background-color: rgb(54, 55, 85);
  color: #ffffff;
}

.settings-panel {
  position: absolute;
  top: 62px;
  right: 18px;
  z-index: 19;
  width: max-content;
  max-width: calc(100vw - 36px);
  padding: 12px 14px;
  border-radius: 8px;
  background-color: rgba(255, 255, 255, 0.96);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.18);
  border: 1px solid rgba(54, 55, 85, 0.16);
}

.settings-panel .model-selection {
  display: grid;
  grid-template-columns: auto 260px;
  justify-content: start;
  align-items: center;
  gap: 9px 12px;
  margin: 0;
  width: auto;
  max-width: none;
}

.settings-panel .model-selection label {
  justify-self: end;
  white-space: nowrap;
}

.settings-panel select {
  width: 260px;
  max-width: 260px;
}

.model-selection {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-top: 0;
}
.status-bar {
  text-align: center;
  font-size: 13px;
  color: rgb(54, 55, 85);
  margin-top: 4px;
  margin-bottom: 4px;
  font-weight: 500;
}

label {
  font-size: 16px;
  font-weight: bold;
  color: #333;
  white-space: nowrap;
  font-family: 'Montserrat', sans-serif;
}

#topKSelect,
#providerSelect,
#modelSelect,
#questionModeSelect,
#workspaceSelect {
  width: auto;
  padding: 10px;
  font-size: 18px;
  border: 2px solid rgb(54, 55, 85);
  border-radius: 8px;
  background-color: #f8f9fa;
  color: rgb(54, 55, 85);
  outline: none;
  cursor: pointer;
  transition: all 0.3s ease-in-out;
  font-family: 'Montserrat', sans-serif;
}
#providerSelect {
  max-width: 260px;
}

#modelSelect,
#workspaceSelect {
  max-width: 240px;
}

/* Background Image Styling */
.background-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: -1;
  opacity:0.3;
}

/* Disable input and button when loading */
input:disabled, button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.references-container {
  margin-top: 10px;
  padding: 10px;
  background-color: rgba(255, 255, 255, 0.8);
  border-radius: 5px;
  text-align: left;
  font-size: 14px;
}
.references-container h3 {
  font-size: 14px;
  color: #333;
  margin-bottom: 5px;
  border-bottom: 1px solid #ddd;
  padding-bottom: 3px;
}
.references-container ul {
  padding-left: 15px;
}
.references-container li {
  list-style: none;
  padding: 5px 0;
}

.reference-title {
  font-weight: 500;
}

.reference-preview {
  margin-top: 4px;
  padding-left: 20px;
}

.reference-preview summary {
  cursor: pointer;
  color: rgb(62, 58, 98);
  font-weight: 600;
}

.reference-preview p {
  margin-top: 6px;
  padding: 8px;
  border-radius: 6px;
  background-color: rgba(240, 240, 240, 0.9);
  line-height: 1.4;
  white-space: pre-wrap;
}

.feedback-container {
  margin-top: 10px;
  padding-top: 8px;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.feedback-label {
  font-size: 12px;
  color: #555;
}

.feedback-button {
  padding: 4px 10px;
  font-size: 12px;
  border-radius: 14px;
  background-color: rgba(255, 255, 255, 0.7);
  color: rgb(62, 58, 98);
  border: 1px solid rgba(62, 58, 98, 0.25);
}

.feedback-button:hover:not(:disabled) {
  background-color: rgb(62, 58, 98);
  color: #ffffff;
  border-color: rgb(62, 58, 98);
}

.feedback-button:disabled:not(.feedback-button-selected) {
  opacity: 0.4;
  cursor: default;
}

.feedback-button:disabled {
  cursor: default;
}

.feedback-button-selected {
  background-color: rgb(74, 154, 143);
  color: #ffffff;
  border-color: rgb(74, 154, 143);
}

.feedback-comment-container {
  margin-top: 6px;
  display: flex;
  gap: 6px;
}

.feedback-comment-input {
  flex: 1;
  padding: 4px 8px;
  font-size: 12px;
  border-radius: 6px;
  border: 1px solid rgba(62, 58, 98, 0.25);
}

.feedback-comment-submit {
  padding: 4px 10px;
  font-size: 12px;
  border-radius: 6px;
  background-color: rgb(62, 58, 98);
  color: #ffffff;
  border: 1px solid rgb(62, 58, 98);
}

.feedback-thanks {
  font-size: 12px;
  color: rgb(74, 154, 143);
  font-weight: 600;
}

.top-container{
  position: fixed;
  left: 0;
  top: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
}

.logo {
  max-width: 155px;
  padding: 8px 12px 4px 12px;
}

.loading-message {  
  font-weight: bold;
  font-size: 16px;
  padding: 12px 16px;
  border-radius: 8px;
  text-align: center;
  color: rgb(247, 204, 101); 
  animation: pulse-scale 1.5s infinite ease-in-out;
  box-shadow: 0 0 12px rgba(74, 154, 143, 0.6);
  transition: all 0.3s ease;  
}

/* Smooth pulsing and scaling effect with color shift */
@keyframes pulse-scale {
  0% { 
    transform: scale(1); 
    opacity: 0.2; 
    box-shadow: 0 0 15px rgba(74, 154, 143, 0.6);
  }
  50% { 
    transform: scale(1.1); 
    opacity: 1; 
    box-shadow: 0 0 25px rgba(44, 184, 173, 0.8); 
  }
  100% { 
    transform: scale(1); 
    opacity: 0.2; 
    box-shadow: 0 0 15px rgba(74, 154, 143, 0.6);
  }
}

/* Responsive layout */
* {
  box-sizing: border-box;
}

html,
body,
#app {
  width: 100%;
  height: 100%;
  min-height: 100%;
}

body {
  min-height: 100dvh;
}

.container {
  position: relative;
  width: 100vw;
  height: 100dvh;
  min-height: 0;
  overflow: hidden;
}

.top-container {
  position: relative;
  left: auto;
  top: auto;
  width: 100%;
  height: 100dvh;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding: clamp(10px, 1.8vw, 18px) clamp(10px, 3vw, 34px) 0;
}

.logo-container {
  position: absolute;
  top: clamp(8px, 1.4vw, 18px);
  left: clamp(8px, 1.4vw, 18px);
  z-index: 3;
}

.logo {
  width: clamp(94px, 12vw, 155px);
  max-width: none;
  height: auto;
  display: block;
  padding: 0;
}

.panoptes-title {
  margin-top: 0;
}

.model-selection {
  flex-wrap: wrap;
  row-gap: 8px;
  max-width: min(980px, calc(100vw - 32px));
  margin: 4px auto 0;
}

#providerSelect,
#modelSelect,
#questionModeSelect,
#workspaceSelect {
  min-width: 0;
  max-width: min(260px, 42vw);
}

.status-bar {
  min-height: 18px;
}

.chat-box {
  position: relative;
  top: auto;
  bottom: auto;
  left: auto;
  transform: none;
  flex: 1 1 auto;
  justify-self: center;
  align-self: center;
  width: min(82vw, 1420px);
  max-width: calc(100vw - 48px);
  min-height: 0;
  height: auto;
  margin: 12px 0 10px;
  padding: 10px 10px 28px;
}

.message-content,
.references-container,
.reference-preview p {
  overflow-wrap: anywhere;
}

.input-footer-container {
  position: relative;
  left: auto;
  bottom: auto;
  transform: none;
  flex: 0 0 auto;
  justify-self: center;
  align-self: center;
  width: min(90vw, 1800px);
  max-width: calc(100vw - 24px);
  flex-shrink: 0;
}

.input-container {
  box-sizing: border-box;
}

.input-container input[type="text"] {
  flex: 1 1 auto;
  min-width: 0;
  width: auto;
}

@media (max-width: 1180px) {
  h1 {
    font-size: 24px;
  }

  .top-container {
    padding-top: 12px;
  }

  .model-selection {
    gap: 7px;
  }

  label {
    font-size: 14px;
  }

  #topKSelect,
  #providerSelect,
  #modelSelect,
  #questionModeSelect,
  #workspaceSelect {
    padding: 8px 9px;
    font-size: 15px;
    max-width: 220px;
  }

  .chat-box {
    width: calc(100vw - 40px);
  }

  .input-footer-container {
    width: calc(100vw - 34px);
  }
}

@media (max-width: 820px) {
  h1 {
    font-size: 22px;
    margin-top: 38px;
  }

  .top-container {
    padding: 10px 10px 0;
  }

  .logo {
    width: 104px;
  }

  .model-selection {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    justify-content: stretch;
    align-items: center;
    gap: 7px 8px;
    width: min(560px, calc(100vw - 20px));
    margin-top: 8px;
  }

  .model-selection label {
    justify-self: end;
    font-size: 13px;
  }

  #providerSelect,
  #modelSelect,
  #questionModeSelect,
  #workspaceSelect {
    width: 100%;
    max-width: none;
    font-size: 14px;
  }

  .status-bar {
    font-size: 12px;
  }

  .chat-box {
    width: calc(100vw - 20px);
    max-width: none;
    margin: 8px 0;
    padding: 8px 8px 22px;
    border-radius: 8px;
  }

  .welcome-panel {
    width: 100%;
    padding: 12px;
  }

  .welcome-message {
    font-size: 15px;
    line-height: 1.45;
  }

  .welcome-examples {
    grid-template-columns: 1fr;
    gap: 8px;
  }

  .welcome-example-button {
    min-height: 48px;
    font-size: 12px;
  }

  .chat-message {
    max-width: 100%;
    font-size: 14px;
  }

  .references-container {
    font-size: 12px;
  }

  .input-footer-container {
    width: 100%;
    max-width: none;
  }

  .input-container {
    padding: 10px;
    gap: 8px;
    border-radius: 0;
  }

  input[type="text"] {
    padding: 10px 12px;
    font-size: 14px;
    margin-right: 0;
  }

  button {
    padding: 10px 13px;
    font-size: 14px;
  }

  .clear-button {
    max-width: 78px;
    line-height: 1.05;
  }
}

@media (max-width: 560px) {
  h1 {
    font-size: 20px;
    margin-top: 34px;
  }

  .logo {
    width: 88px;
  }

  .model-selection {
    grid-template-columns: 1fr;
    gap: 4px;
    margin-top: 6px;
  }

  .model-selection label {
    justify-self: start;
    font-size: 12px;
  }

  #providerSelect,
  #modelSelect,
  #questionModeSelect,
  #workspaceSelect {
    padding: 7px 8px;
    font-size: 13px;
  }

  .status-bar {
    font-size: 11px;
    margin-top: 3px;
  }

  .chat-box {
    width: calc(100vw - 12px);
    margin: 6px 0;
    padding: 7px 6px 18px;
  }

  .welcome-panel {
    padding: 8px;
  }

  .welcome-message {
    margin-bottom: 10px;
    font-size: 13px;
  }

  .chat-message {
    padding: 8px;
    border-radius: 10px;
    font-size: 13px;
  }

  .message-content {
    line-height: 1.42;
  }

  .bot-message ul {
    margin-left: 12px;
    padding-left: 14px;
  }

  .references-container {
    padding: 8px;
  }

  .feedback-container,
  .feedback-comment-container {
    gap: 5px;
  }

  .feedback-label {
    width: 100%;
  }

  .feedback-button {
    padding: 4px 8px;
    font-size: 11px;
  }

  .input-container {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto auto;
    padding: 8px;
  }

  input[type="text"] {
    font-size: 13px;
    padding: 9px 10px;
  }

  button {
    padding: 9px 10px;
    font-size: 13px;
  }

  .clear-button {
    width: 58px;
  }

}

@media (max-width: 380px) {
  h1 {
    font-size: 18px;
    margin-top: 30px;
  }

  .logo {
    width: 78px;
  }

  button {
    padding-inline: 8px;
    font-size: 12px;
  }

  .clear-button {
    width: 52px;
  }
}

@media (max-height: 680px) and (max-width: 820px) {
  h1 {
    margin-top: 30px;
    font-size: 18px;
  }

  .logo {
    width: 80px;
  }

  .model-selection {
    grid-template-columns: auto minmax(0, 1fr);
  }
}


.copy-answer-icon {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  align-self: flex-start;
  width: 28px;
  height: 28px;
  margin-top: 8px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #6b7280;
  cursor: pointer;
}

.copy-answer-icon:hover {
  background: #f3f4f6;
  color: #111827;
}

.copy-answer-symbol,
.copy-answer-symbol::before {
  position: absolute;
  width: 13px;
  height: 13px;
  border: 2px solid currentColor;
  border-radius: 4px;
  box-sizing: border-box;
}

.copy-answer-symbol {
  transform: translate(2px, 2px);
}

.copy-answer-symbol::before {
  content: "";
  transform: translate(-5px, -5px);
  background: #ffffff;
}

.copy-answer-check {
  font-size: 18px;
  line-height: 1;
  color: #2563eb;
}

</style>
